import { Input } from "@/components/ui/input";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

const FILTER_ALL = "__all__" as const;

function toParam(v: string): string | undefined {
  return v === FILTER_ALL || !v ? undefined : v;
}

type AnnouncementsSearch = {
  isActive?: string;
  priority?: string;
  search?: string;
  offset?: number;
  limit?: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
};

const PRIORITY_OPTIONS = [
  { value: FILTER_ALL, label: "ທຸກລຳດັບ" },
  { value: "URGENT", label: "ດ່ວນ" },
  { value: "HIGH", label: "ສຳຄັນ" },
  { value: "NORMAL", label: "ທົ່ວໄປ" },
];

export function AnnouncementsFilter() {
  const nav = useNavigate({ from: "/app/announcements" });
  const search = useSearch({
    from: "/app/announcements",
  }) as AnnouncementsSearch;

  const hasFilter =
    !!search.isActive || !!search.search || !!search.priority;

  const [searchInputValue, setSearchInputValue] = useState<string>(
    search.search ?? "",
  );

  useEffect(() => {
    setSearchInputValue(search.search ?? "");
  }, [search.search]);

  const debouncedSearch = useDebounceCallback((val: string) => {
    update({ search: val || undefined });
  }, 400);

  const update = (next: Partial<AnnouncementsSearch>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  const clearFilters = () => {
    setSearchInputValue("");
    nav({
      search: {
        offset: search.offset ?? 0,
        limit: search.limit ?? 20,
        sortField: search.sortField,
        sortOrder: search.sortOrder,
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 pb-2">
      <Tabs
        className="w-full sm:w-auto"
        value={
          search.isActive === "true" || search.isActive === "false"
            ? search.isActive
            : FILTER_ALL
        }
        onValueChange={(v) => update({ isActive: toParam(v ?? "") })}
      >
        <TabsList className="h-9 flex-wrap gap-1 bg-muted/50">
          <TabsTrigger value={FILTER_ALL} className="text-xs">
            ທຸກສະຖານະ
          </TabsTrigger>
          <TabsTrigger value="true" className="text-xs">
            ເຜີຍແຜ່
          </TabsTrigger>
          <TabsTrigger value="false" className="text-xs">
            ປິດ
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <SimpleSelect
        className="h-9 w-40"
        value={search.priority ?? FILTER_ALL}
        options={PRIORITY_OPTIONS}
        onValueChange={(v) =>
          update({
            priority: v === FILTER_ALL ? undefined : v,
          })
        }
      />

      <Input
        type="search"
        placeholder="ຄົ້ນຫາຫົວຂໍ້..."
        className="h-9 w-56"
        value={searchInputValue}
        onChange={(e) => {
          const v = e.target.value;
          setSearchInputValue(v);
          debouncedSearch(v);
        }}
      />

      {hasFilter ? (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="size-4" />
          ລ້າງຕົວກອງ
        </button>
      ) : null}
    </div>
  );
}
